import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useUserPaymentContext } from './UserPaymentProvider';

import { FEATURE_FLAGS } from '~common';
import { useQueryGetStationPricing } from '~hooks/useQueryGetStationPricing';
import { useQueryGetStationPricings } from '~hooks/useQueryGetStationPricings';
import { inMemoryJWTService } from '~services/jwt.service';
import {
  Pricing,
  PricingDisplayModel,
  StationPricingModel,
} from '~types/pricing.type';
import { checkEnabledFeatureFlag } from '~utils';

type StationPricingContextType = {
  stationPricing: Pricing | null;
  stationPricingDisplay: PricingDisplayModel | null;
  secondStationPricing: Pricing | null;
  secondStationPricingDisplay: PricingDisplayModel | null;
  /**
   * The pass catalog for this station, already de-duplicated against the first
   * and second pricings by the backend — render it alongside them.
   */
  subscriptionPricings: StationPricingModel[];
  /**
   * Whether the station offers more than one thing to buy, counting the pass
   * catalog. Gates the pricing-selection step and its back button — a station
   * with a single option has nothing to pick between.
   */
  hasMultiplePricingOptions: boolean;
  allowSubscription: boolean;
  isLoadingStationPricing: boolean;
};

const StationPricingContext = createContext<StationPricingContextType>({
  stationPricing: null,
  stationPricingDisplay: null,
  secondStationPricing: null,
  secondStationPricingDisplay: null,
  subscriptionPricings: [],
  hasMultiplePricingOptions: false,
  allowSubscription: true,
  isLoadingStationPricing: false,
});

export const StationPricingProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();

  const stationImeiOrSerialNumber = searchParams.get('box') || '';

  const isDualPricingFlowEnabled = checkEnabledFeatureFlag(
    FEATURE_FLAGS.WEB_APP_DUAL_PRICING_FLOW,
  );

  const isSubscriptionPassEnabled = checkEnabledFeatureFlag(
    FEATURE_FLAGS.WEB_APP_SUBSCRIPTION_PASS,
  );

  const { paymentInfo } = useUserPaymentContext();
  const paymentMethodId = paymentInfo?.paymentMethodId;

  const shouldEnableQueries =
    !!stationImeiOrSerialNumber && !!inMemoryJWTService.getCurrentToken();

  const queryGetStationPricing = useQueryGetStationPricing(
    {
      serialNumberOrImei: stationImeiOrSerialNumber,
      paymentMethodId,
    },
    {
      enabled: !isDualPricingFlowEnabled && shouldEnableQueries,
    },
  );

  const queryGetStationPricings = useQueryGetStationPricings(
    {
      serialNumberOrImei: stationImeiOrSerialNumber,
      paymentMethodId,
    },
    {
      enabled: isDualPricingFlowEnabled && shouldEnableQueries,
    },
  );

  const firstPricingModel = useMemo(() => {
    if (queryGetStationPricings.data) {
      return {
        ...queryGetStationPricings.data.firstPricing.model,
        symbol: queryGetStationPricings.data.symbol,
      };
    }

    return queryGetStationPricing.data || null;
  }, [queryGetStationPricing.data, queryGetStationPricings.data]);

  const secondPricingModel = useMemo(() => {
    if (
      queryGetStationPricings.data &&
      queryGetStationPricings.data.secondPricing
    ) {
      return {
        ...queryGetStationPricings.data.secondPricing.model,
        symbol: queryGetStationPricings.data.symbol,
      };
    }

    return null;
  }, [queryGetStationPricings.data]);

  const subscriptionPricings = useMemo(() => {
    // The backend ships the pass catalog whether or not this client is ready to
    // sell it. Without this gate the flag would only cover pass *management*
    // while the catalog went live on its own — users could buy a pass they then
    // had no screen to manage.
    if (!isSubscriptionPassEnabled) {
      return [];
    }

    const symbol = queryGetStationPricings.data?.symbol;

    return (queryGetStationPricings.data?.subscriptionPricings ?? []).map(
      (pricing) => ({
        ...pricing,
        model: { ...pricing.model, symbol },
      }),
    );
  }, [queryGetStationPricings.data, isSubscriptionPassEnabled]);

  const providerValues = useMemo(
    () => ({
      stationPricing: firstPricingModel,
      stationPricingDisplay:
        queryGetStationPricings.data?.firstPricing.displayModel || null,
      secondStationPricing: secondPricingModel,
      secondStationPricingDisplay:
        queryGetStationPricings.data?.secondPricing?.displayModel || null,
      subscriptionPricings,
      hasMultiplePricingOptions:
        !!secondPricingModel || subscriptionPricings.length > 0,
      allowSubscription:
        queryGetStationPricings.data?.allowSubscription ?? true,
      isLoadingStationPricing:
        queryGetStationPricings.isFetching || queryGetStationPricing.isFetching,
    }),
    [
      queryGetStationPricing.isFetching,
      queryGetStationPricings.isFetching,
      queryGetStationPricings.data,
      firstPricingModel,
      secondPricingModel,
      subscriptionPricings,
    ],
  );

  return (
    <StationPricingContext.Provider value={providerValues}>
      {children}
    </StationPricingContext.Provider>
  );
};

export const useStationPricingContext = () => useContext(StationPricingContext);
